import { c as _c } from "react/compiler-runtime";
import { ForecastDrawn, AmountTail, zAmountTail, ForecastLogistic, QuestionCfg } from 'shared/forecast';
import { fmtNum } from 'shared/format';
import { Tooltip, ActionIcon, CloseButton, InputWrapper, Popover } from '@mantine/core';
import { clamp } from 'remeda';
import { StringNumberInput } from 'components/form/StringNumberInput';
import { TbArrowBarLeft, TbArrowBarRight, TbArrowBarToLeft, TbArrowBarToRight } from 'react-icons/tb';
import { Slider } from 'components/form/Slider';
import { useState } from 'react';
import { DeltaValues } from 'shared/paths';
import { notNull } from 'shared/type';
import { match } from 'ts-pattern';
import { twMerge } from 'tailwind-merge';
import { TailIcon } from 'shared/forecast/results';
import { ScaleLinear } from 'd3-scale';
export const useHardBoundTails = ({
  forecast,
  question,
  onDelta
}: {
  forecast: Pick<ForecastLogistic | ForecastDrawn, 'hardBounds' | 'tails'>;
  question: Pick<QuestionCfg, 'hardBounds'>;
  onDelta: (value: DeltaValues<ForecastLogistic>) => void;
}) => {
  const [tailEditing, setTailEditing] = useState({
    left: false,
    right: false
  });
  const tailProps = (side: 'left' | 'right'): Omit<TailEditProps, 'xScale'> | undefined => {
    const hardBounds = forecast.hardBounds ?? question.hardBounds;
    const bound = match(side).with('left', () => hardBounds?.min).with('right', () => hardBounds?.max).exhaustive();
    if (bound == null) return undefined;
    return {
      type: 'bound',
      side: side,
      tails: forecast.tails,
      onChange: v => onDelta({
        [`tails.${side}`]: v
      }),
      opened: tailEditing[side],
      setOpened: v_0 => setTailEditing({
        ...tailEditing,
        [side]: v_0
      }),
      pt: {
        x: bound
      }
    };
  };
  const tails = [tailProps('left'), tailProps('right')].filter(notNull);
  return tails;
};
export type TailEditProps = {
  /** domain coordinates (p should be between 0 and 1) */
  pt?: {
    x?: number;
    p?: number;
  };
  side: 'left' | 'right';
  type: 'tail' | 'bound';
  tails: ForecastDrawn['tails'];
  onChange: (v: AmountTail | undefined) => void;
  opened: boolean;
  setOpened: (v: boolean) => void;
  fmt?: (v: number) => string;
  xScale: ScaleLinear<number, number>;
  yScale?: (y: number) => number;
  debugName?: string;
};
export const ForecastAmountTailEdit = t0 => {
  const $ = _c(81);
  const {
    pt,
    side,
    type,
    tails,
    onChange,
    opened,
    setOpened,
    className,
    fmt: t1,
    xScale,
    yScale
  } = t0;
  const fmt = t1 === undefined ? fmtNum : t1;
  const tail = tails[side];
  const tailOther = tails[side == "left" ? "right" : "left"];
  let t2;
  if ($[0] !== onChange || $[1] !== tail || $[2] !== tailOther?.pct) {
    t2 = pct => {
      const delta = {
        ...(tail ?? zAmountTail.parse({})),
        pct: clamp(pct, {
          min: 0,
          max: 99.1 - (tailOther?.pct ?? 0)
        })
      };
      onChange?.(delta);
    };
    $[0] = onChange;
    $[1] = tail;
    $[2] = tailOther?.pct;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const onChangePct = t2;
  let t3;
  if ($[4] !== pt || $[5] !== xScale || $[6] !== yScale) {
    t3 = pt ? {
      left: pt?.x != null ? xScale(pt.x) : 0,
      top: (pt?.p != null ? yScale?.(pt.p) : null) ?? "50%"
    } : {
      top: "50%"
    };
    $[4] = pt;
    $[5] = xScale;
    $[6] = yScale;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const positionStyle = t3;
  const [xMin, xMax] = xScale.domain() as [number, number];
  const domainWidth = xMax - xMin;
  const padding = domainWidth * 0.1;
  if (pt?.x != null && (pt.x < xMin - padding || pt.x > xMax + padding)) {
    return null;
  }
  let t4;
  if ($[8] !== className) {
    t4 = twMerge("absolute", className);
    $[8] = className;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const t5 = `translate(${side == "left" ? "calc(-100% - 4px)" : "6px"}, -60%)`;
  let t6;
  if ($[10] !== positionStyle || $[11] !== t5) {
    t6 = {
      transform: t5,
      ...positionStyle
    };
    $[10] = positionStyle;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const t7 = side == "left" ? "top-start" : "top-end";
  let t8;
  if ($[13] !== pt?.x || $[14] !== side || $[15] !== tail || $[16] !== type) {
    t8 = tail?.pct ? <div><b>{fmtNum(tail.pct)}%</b>{match(type).with("tail", () => ` ${side == "left" ? "below" : "above"} ${pt?.x ?? "this bound"}`).with("bound", () => ` at ${pt?.x ?? "this bound"}`).exhaustive()}</div> : `no ${type} percentage`;
    $[13] = pt?.x;
    $[14] = side;
    $[15] = tail;
    $[16] = type;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const t9 = side == "left" ? "end" : "start";
  const t10 = tail?.pct ? 0.7 : 0.3;
  let t11;
  if ($[18] !== t10 || $[19] !== t9) {
    t11 = {
      alignItems: t9,
      opacity: t10
    };
    $[18] = t10;
    $[19] = t9;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== setOpened) {
    t12 = () => setOpened(true);
    $[21] = setOpened;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== side || $[24] !== type) {
    t13 = <TailIcon {...{
      side,
      mode: type
    }} />;
    $[23] = side;
    $[24] = type;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t12 || $[27] !== t13) {
    t14 = <ActionIcon className="mb-[-4px]" size="md" onClick={t12}>{t13}</ActionIcon>;
    $[26] = t12;
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== tail) {
    t15 = tail?.pct ? <div className="text-sm"><b>{fmtNum(tail.pct)}%</b></div> : undefined;
    $[29] = tail;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== t11 || $[32] !== t14 || $[33] !== t15) {
    t16 = <div className="flex flex-col" style={t11}>{t14}{t15}</div>;
    $[31] = t11;
    $[32] = t14;
    $[33] = t15;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t16 || $[36] !== t8) {
    t17 = <Popover.Target><Tooltip label={t8}>{t16}</Tooltip></Popover.Target>;
    $[35] = t16;
    $[36] = t8;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== fmt || $[39] !== pt || $[40] !== side || $[41] !== type) {
    let t19;
    if ($[43] !== fmt || $[44] !== pt || $[45] !== side) {
      t19 = () => `percentage ${side == "left" ? "below" : "above"} ${pt?.x ? fmt(pt.x) : ""}`;
      $[43] = fmt;
      $[44] = pt;
      $[45] = side;
      $[46] = t19;
    } else {
      t19 = $[46];
    }
    let t20;
    if ($[47] !== pt?.x) {
      t20 = () => `percentage at ${pt?.x ?? "bound"}`;
      $[47] = pt?.x;
      $[48] = t20;
    } else {
      t20 = $[48];
    }
    t18 = match(type).with("tail", t19).with("bound", t20).exhaustive();
    $[38] = fmt;
    $[39] = pt;
    $[40] = side;
    $[41] = type;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[49] !== t18) {
    t19 = <div>{t18}</div>;
    $[49] = t18;
    $[50] = t19;
  } else {
    t19 = $[50];
  }
  const t20 = tail?.pct ?? 0;
  let t21;
  if ($[51] !== onChange) {
    t21 = <CloseButton onClick={() => onChange(undefined)} />;
    $[51] = onChange;
    $[52] = t21;
  } else {
    t21 = $[52];
  }
  let t22;
  if ($[53] !== onChangePct || $[54] !== t20 || $[55] !== t21) {
    t22 = <StringNumberInput className="w-[8em]" value={t20} onChange={onChangePct} rightSection={t21} />;
    $[53] = onChangePct;
    $[54] = t20;
    $[55] = t21;
    $[56] = t22;
  } else {
    t22 = $[56];
  }
  let t23;
  if ($[57] !== t19 || $[58] !== t22) {
    t23 = <div className="flex gap-2 items-center" onMouseDownCapture={_temp} onMouseDown={_temp2}>{t19}{t22}</div>;
    $[57] = t19;
    $[58] = t22;
    $[59] = t23;
  } else {
    t23 = $[59];
  }
  let t24;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = {
      pct: {}
    };
    $[60] = t24;
  } else {
    t24 = $[60];
  }
  const t25 = tail?.pct ?? 0;
  let t26;
  if ($[61] !== t25) {
    t26 = {
      pct: t25
    };
    $[61] = t25;
    $[62] = t26;
  } else {
    t26 = $[62];
  }
  let t27;
  if ($[63] !== onChangePct) {
    t27 = t28 => {
      const {
        value: v
      } = t28;
      onChangePct(Math.round(v));
    };
    $[63] = onChangePct;
    $[64] = t27;
  } else {
    t27 = $[64];
  }
  let t28;
  if ($[65] !== t26 || $[66] !== t27) {
    t28 = <InputWrapper><Slider className="w-full mt-2" thumbs={t24} max={99.9} value={t26} onChange={t27} /></InputWrapper>;
    $[65] = t26;
    $[66] = t27;
    $[67] = t28;
  } else {
    t28 = $[67];
  }
  let t29;
  if ($[68] !== t23 || $[69] !== t28) {
    t29 = <Popover.Dropdown>{t23}{t28}</Popover.Dropdown>;
    $[68] = t23;
    $[69] = t28;
    $[70] = t29;
  } else {
    t29 = $[70];
  }
  let t30;
  if ($[71] !== opened || $[72] !== setOpened || $[73] !== t17 || $[74] !== t29 || $[75] !== t7) {
    t30 = <Popover withinPortal={true} opened={opened} onChange={setOpened} position={t7}>{t17}{t29}</Popover>;
    $[71] = opened;
    $[72] = setOpened;
    $[73] = t17;
    $[74] = t29;
    $[75] = t7;
    $[76] = t30;
  } else {
    t30 = $[76];
  }
  let t31;
  if ($[77] !== t30 || $[78] !== t4 || $[79] !== t6) {
    t31 = <div className={t4} style={t6}>{t30}</div>;
    $[77] = t30;
    $[78] = t4;
    $[79] = t6;
    $[80] = t31;
  } else {
    t31 = $[80];
  }
  return t31;
};
function _temp(e) {
  return e.stopPropagation();
}
function _temp2(e_0) {
  return e_0.stopPropagation();
}